import { useState } from "react"
import { useDispatch, useSelector } from "react-redux"

import Button from "../../common/Button"
import BodySimpleModalV3 from "../../common/modal/BodySimpleModalV3"
import {
    allowsEmptySavedWorkQueueFilters,
    applyStandardWorkQueueDefaults,
    currentMatchesSavedWorkQueueView,
    sanitizeExceptionsForSavedView,
    sanitizeFiltersForSavedView,
    setFiltersInLocalStorage,
} from "../../../lib/filters"
import { deleteWorkQueueMyView, saveWorkQueueMyView } from "../../../lib/fetchApi"
import { filterActions } from "../../../store/filter"
import { workqueueActions } from "../../../store/workqueue"
import { notificationActions } from "../../../store/notifications"
import { getItemFromStorageSimple } from "../../../lib/script"
import classes from "./SavedViewBanner.module.css"

function buildSavedViewPayload(filters, exceptions, workQ) {
    return {
        filters: sanitizeFiltersForSavedView(filters, workQ),
        exceptions: sanitizeExceptionsForSavedView(exceptions),
    }
}

function hasMeaningfulSavedPayload(payload) {
    const hasFilters = payload?.filters && Object.keys(payload.filters).length > 0
    const hasExceptions = payload?.exceptions && Object.keys(payload.exceptions).length > 0
    return !!(hasFilters || hasExceptions)
}

function canSavePayload(payload, workQ) {
    if (hasMeaningfulSavedPayload(payload)) {
        return true
    }
    return allowsEmptySavedWorkQueueFilters(workQ?.slug)
}

function persistEmptySavedFilters(workQ, filters) {
    if (!allowsEmptySavedWorkQueueFilters(workQ?.slug)) {
        return
    }
    if (!filters || typeof filters !== 'object' || Array.isArray(filters)) {
        return
    }
    if (Object.keys(filters).length > 0) {
        return
    }
    setFiltersInLocalStorage({}, workQ.slug)
}

function apiErrorMessage(result, fallback) {
    if (!result?.errorMessage) {
        return null
    }
    if (typeof result.errorMessage === "string") {
        return result.errorMessage
    }
    const filtersError = result.errorMessage?.errors?.filters
    if (Array.isArray(filtersError) && filtersError[0]) {
        return filtersError[0]
    }
    if (typeof result.errorMessage?.message === "string") {
        return result.errorMessage.message
    }
    return fallback
}

export function SaveWorkQueueViewButton({ workQ }) {
    const dispatch = useDispatch()
    const token = useSelector((state) => state.user.token) || localStorage.getItem("token")
    const filters = useSelector((state) => state.filter.filters)
    const exceptions = useSelector((state) => state.filter.exceptions)
    const savedView = useSelector((state) => (
        workQ?.slug ? state.filter.savedViewsBySlug?.[workQ.slug] : null
    ))
    const [isSaving, setIsSaving] = useState(false)

    if (!workQ?.slug) {
        return null
    }

    const payload = buildSavedViewPayload(filters, exceptions, workQ)
    const matchesSaved = currentMatchesSavedWorkQueueView(workQ, filters, exceptions, savedView)

    async function saveHandler() {
        if (isSaving || matchesSaved) {
            return
        }
        if (!canSavePayload(payload, workQ)) {
            dispatch(notificationActions.setError({
                errorMessage: "Select at least one filter to save.",
            }))
            return
        }
        if (savedView && !window.confirm("Replace your saved filter for this queue with the current filters?")) {
            return
        }

        setIsSaving(true)
        try {
            const result = await saveWorkQueueMyView(token, workQ.slug, payload)
            const error = apiErrorMessage(result, "Could not save your filter.")
            if (error) {
                dispatch(notificationActions.setError({ errorMessage: error }))
                return
            }
            const savedFilters = result?.filters || payload.filters
            dispatch(filterActions.upsertSavedView({
                slug: result?.slug || workQ.slug,
                filters: savedFilters,
                exceptions: result?.exceptions || payload.exceptions,
                updated_at: result?.updated_at || null,
            }))
            persistEmptySavedFilters(workQ, payload.filters)
            dispatch(filterActions.setActiveSavedViewSlug(workQ.slug))
            dispatch(notificationActions.setError({
                errorMessage: "Your filter for this queue is saved.",
                type: "success",
            }))
        } finally {
            setIsSaving(false)
        }
    }

    return (
        <BodySimpleModalV3 message={savedView ? "Save filter" : "Save filter"}>
            <Button
                click={saveHandler}
                isDisabled={matchesSaved}
                isLoading={isSaving}
                bypassisCaseArchived={true}
                title="Save filter"
            >
                <i className="ti ti-bookmark"></i> Save filter
            </Button>
        </BodySimpleModalV3>
    )
}

export default function SavedViewBanner({ workQ }) {
    const dispatch = useDispatch()
    const token = useSelector((state) => state.user.token) || localStorage.getItem("token")
    const filters = useSelector((state) => state.filter.filters)
    const exceptions = useSelector((state) => state.filter.exceptions)
    const savedView = useSelector((state) => (
        workQ?.slug ? state.filter.savedViewsBySlug?.[workQ.slug] : null
    ))
    const [isBusy, setIsBusy] = useState(false)

    if (!workQ?.slug || !savedView) {
        return null
    }

    const payload = buildSavedViewPayload(filters, exceptions, workQ)
    const filtersDiffer = !currentMatchesSavedWorkQueueView(workQ, filters, exceptions, savedView)

    async function useStandardHandler() {
        if (isBusy) {
            return
        }
        setIsBusy(true)
        try {
            const result = await deleteWorkQueueMyView(token, workQ.slug)
            if (result?.errorMessage) {
                dispatch(notificationActions.setError({
                    errorMessage: "Could not clear your saved view.",
                }))
                return
            }
            dispatch(filterActions.removeSavedView(workQ.slug))
            const userLocations = getItemFromStorageSimple("userLocations")
            applyStandardWorkQueueDefaults(workQ, dispatch, userLocations)
            dispatch(workqueueActions.setInitialLoad(null))
            dispatch(filterActions.setCurrentPage(1))
            dispatch(workqueueActions.setRefreshApi())
        } finally {
            setIsBusy(false)
        }
    }

    async function updateHandler() {
        if (isBusy || !filtersDiffer) {
            return
        }
        if (!canSavePayload(payload, workQ)) {
            dispatch(notificationActions.setError({
                errorMessage: "Select at least one filter to save.",
            }))
            return
        }
        setIsBusy(true)
        try {
            const result = await saveWorkQueueMyView(token, workQ.slug, payload)
            const error = apiErrorMessage(result, "Could not update your saved filter.")
            if (error) {
                dispatch(notificationActions.setError({ errorMessage: error }))
                return
            }
            const savedFilters = result?.filters || payload.filters
            dispatch(filterActions.upsertSavedView({
                slug: result?.slug || workQ.slug,
                filters: savedFilters,
                exceptions: result?.exceptions || payload.exceptions,
                updated_at: result?.updated_at || null,
            }))
            persistEmptySavedFilters(workQ, payload.filters)
            dispatch(notificationActions.setError({
                errorMessage: "Your saved filter was updated.",
                type: "success",
            }))
        } finally {
            setIsBusy(false)
        }
    }

    return (
        <div className={classes.banner} role="status">
            <div className={classes.banner_message}>
                <i className="ti ti-bookmark"></i>
                Using your saved view for this queue
            </div>
            <div className={classes.banner_actions}>
                {filtersDiffer && (
                    <Button
                        click={updateHandler}
                        isLoading={isBusy}
                        bypassisCaseArchived={true}
                        btnStyle="btn_dark"
                    >
                        Update saved view
                    </Button>
                )}
                <Button
                    click={useStandardHandler}
                    isLoading={isBusy}
                    bypassisCaseArchived={true}
                >
                    Use standard filters
                </Button>
            </div>
        </div>
    )
}
